// src/pages/firstaider/ComposeCalloutPage.tsx — State 2: the red compose form.
//
// Route: /apps/first-aider. Reached from the featured Apps-page tile.
//
// Structured emergency fields + ONE Send button. NO confirmation modal, no
// swipe-to-send, no "are you sure" — by design (gotcha #1): friction at the
// worst moment can cost a life, and an accidental send is recoverable (respond
// to the thread / mark resolved). Two real blocks only: a caller with no phone
// on profile (no callback path — gotcha #7) and zero first aiders on duty
// (gotcha #15: show "call 999" instead of Send).

import type { CSSProperties, ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { C, F } from "../../theme";
import { ApiError } from "../../api";
import { useCurrentUser } from "../../auth";
import {
  FA,
  STATION_CHIPS,
  fetchOnDutyCount,
  postIncident,
  registeredSignedInCaption,
} from "../../lib/firstAider";
import { FaTopBar, PulseDot, SendGlyph } from "../../components/firstAider/kit";
import { FirstAiderRosterSheet } from "../../components/firstAider/FirstAiderRosterSheet";
import { ActiveCalloutCards } from "../../components/firstAider/ActiveCalloutCards";

const CASUALTY_MAX = 200;
const NATURE_MAX = 300;

export function ComposeCalloutPage() {
  const navigate = useNavigate();
  const { me } = useCurrentUser();

  const [counts, setCounts] = useState<{ registered: number; signedIn: number; alertable: number } | null>(null);
  const [callerPhone, setCallerPhone] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [rosterOpen, setRosterOpen] = useState(false);

  const [location, setLocation] = useState("");
  const [selectedChip, setSelectedChip] = useState<string | null>(null);
  const [casualty, setCasualty] = useState("");
  const [nature, setNature] = useState("");
  const [lifeThreatening, setLifeThreatening] = useState<boolean | null>(null);
  const [called999, setCalled999] = useState<boolean | null>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const openedAt = useMemo(() => new Date(), []);

  useEffect(() => {
    let cancelled = false;
    fetchOnDutyCount()
      .then((res) => {
        if (cancelled) return;
        setCounts({ registered: res.registeredCount, signedIn: res.signedInCount, alertable: res.alertableCount });
        setCallerPhone(res.callerPhone);
      })
      .catch(() => {
        if (!cancelled) {
          setCounts({ registered: 0, signedIn: 0, alertable: 0 });
          setCallerPhone(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const hasPhone = !!callerPhone;
  const zeroAlertable = loaded && (counts?.alertable ?? 0) === 0;
  const callerName = `${me?.first ?? ""} ${me?.last ?? ""}`.trim();

  const canSend =
    hasPhone &&
    !zeroAlertable &&
    location.trim().length > 0 &&
    casualty.trim().length > 0 &&
    nature.trim().length > 0 &&
    lifeThreatening !== null &&
    called999 !== null &&
    !sending;

  async function onSend() {
    if (!canSend) return;
    setSending(true);
    setError(null);
    try {
      const res = await postIncident({
        location: location.trim(),
        casualty: casualty.trim(),
        nature: nature.trim(),
        isLifeThreatening: lifeThreatening!,
        called999: called999!,
      });
      navigate(`/first-aider/incidents/${res.incidentId}`, { replace: true });
    } catch (e) {
      setSending(false);
      if (e instanceof ApiError) {
        const body = e.body as { error?: string; reason?: string; onDutyCount?: number } | null;
        if (body?.error === "no_caller_phone") {
          setCallerPhone(null);
          setError("Add a phone number to your profile before sending a callout.");
          return;
        }
        if (body?.error === "no_on_duty_first_aiders") {
          setCounts((c) => ({ registered: c?.registered ?? 0, signedIn: c?.signedIn ?? 0, alertable: 0 }));
          return;
        }
        setError(e.message || "Couldn't send the callout. Try again.");
        return;
      }
      setError("Couldn't send the callout. Try again.");
    }
  }

  function pickChip(chip: string) {
    setSelectedChip(chip);
    setLocation((prev) => (prev.trim() ? `${chip}, ${prev}` : chip));
  }

  return (
    <div style={{ background: FA.tint, minHeight: "100vh" }}>
      <FaTopBar title="First Aider Callout" sub="Emergency dispatch" onBack={() => navigate("/apps")} />

      <div style={{ padding: "16px 16px 28px", display: "flex", flexDirection: "column", gap: 16 }}>
        {/* Any already-active callout(s) shown above the form — so a second
            caller sees the live one rather than unknowingly opening a duplicate. */}
        <ActiveCalloutCards />
        <SummaryStrip openedAt={openedAt} name={callerName} phone={callerPhone} onAddPhone={() => navigate("/me/details")} />

        <FormRow label="Location of emergency">
          <FormInput value={location} onChange={setLocation} placeholder="Platform, building, mile post…" />
          <div style={chipRowStyle}>
            {STATION_CHIPS.map((chip) => (
              <button
                key={chip}
                type="button"
                onClick={() => pickChip(chip)}
                aria-pressed={selectedChip === chip}
                style={selectedChip === chip ? chipSelectedStyle : chipStyle}
              >
                {chip}
              </button>
            ))}
          </div>
        </FormRow>

        <FormRow label="Who is injured">
          <FormInput
            value={casualty}
            onChange={setCasualty}
            maxLength={CASUALTY_MAX}
            placeholder="e.g. Elderly passenger, female"
          />
        </FormRow>

        <FormRow label="Life-threatening?">
          <YesNoToggle value={lifeThreatening} onChange={setLifeThreatening} />
        </FormRow>

        <FormRow label="Nature of injury">
          <FormInput
            value={nature}
            onChange={setNature}
            maxLength={NATURE_MAX}
            placeholder="e.g. Suspected fall, head injury, conscious"
          />
        </FormRow>

        <FormRow label="Do you need to call 999?">
          <YesNoToggle value={called999} onChange={setCalled999} />
        </FormRow>

        <Call999Reminder emphatic={zeroAlertable} />

        {error && <div style={errorStyle}>{error}</div>}

        {zeroAlertable ? (
          <>
            <div style={zeroBannerStyle}>No first aiders available to alert.</div>
            <button type="button" onClick={() => setRosterOpen(true)} style={captionButtonStyle}>
              <PulseDot tone="red" />
              {counts ? registeredSignedInCaption(counts.registered, counts.signedIn) : "View first aiders"}
              <span style={tapHintStyle}>· tap to see who</span>
            </button>
          </>
        ) : (
          <>
            <SendButton canSend={canSend} sending={sending} onClick={onSend} />
            {hasPhone ? (
              <button type="button" onClick={() => setRosterOpen(true)} style={captionButtonStyle}>
                <PulseDot tone="red" />
                {counts === null
                  ? "Checking first aiders…"
                  : registeredSignedInCaption(counts.registered, counts.signedIn)}
                {counts !== null && <span style={tapHintStyle}>· tap to see who</span>}
              </button>
            ) : (
              <button type="button" onClick={() => navigate("/me/details")} style={addPhoneStyle}>
                Add your phone number to your profile before sending →
              </button>
            )}
          </>
        )}
      </div>

      <FirstAiderRosterSheet open={rosterOpen} onClose={() => setRosterOpen(false)} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function SummaryStrip({
  openedAt,
  name,
  phone,
  onAddPhone,
}: {
  openedAt: Date;
  name: string;
  phone: string | null;
  onAddPhone: () => void;
}) {
  const when = new Intl.DateTimeFormat("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(openedAt);
  return (
    <div style={summaryStripStyle}>
      <div style={summaryRowStyle}>
        <span style={summaryLabelStyle}>When</span>
        <span style={summaryValueStyle}>{when}</span>
      </div>
      <div style={{ height: 1, background: FA.redLt, opacity: 0.5 }} />
      {/* Caller's name + number so responders know who's reporting (the thread
          also shows this as "Reported by"). */}
      <div style={{ ...summaryRowStyle, alignItems: "flex-start" }}>
        <span style={{ ...summaryLabelStyle, paddingTop: 2 }}>Your contact</span>
        <div style={{ textAlign: "right", minWidth: 0 }}>
          {name && <div style={summaryNameStyle}>{name}</div>}
          {phone ? (
            <div style={{ ...summaryValueStyle, marginTop: name ? 1 : 0 }}>{phone}</div>
          ) : (
            <button type="button" onClick={onAddPhone} style={summaryAddStyle}>
              Add a number →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function FormRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div style={fieldLabelStyle}>{label}</div>
      {children}
    </div>
  );
}

function FormInput({
  value,
  onChange,
  maxLength,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  maxLength?: number;
  placeholder?: string;
}) {
  return (
    <input
      type="text"
      value={value}
      maxLength={maxLength}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      style={inputStyle}
    />
  );
}

function YesNoToggle({
  value,
  onChange,
}: {
  value: boolean | null;
  onChange: (v: boolean) => void;
}) {
  return (
    <div style={{ display: "flex", gap: 8 }}>
      <button
        type="button"
        onClick={() => onChange(true)}
        aria-pressed={value === true}
        style={value === true ? toggleYesStyle : toggleBaseStyle}
      >
        Yes
      </button>
      <button
        type="button"
        onClick={() => onChange(false)}
        aria-pressed={value === false}
        style={value === false ? toggleNoStyle : toggleBaseStyle}
      >
        No
      </button>
    </div>
  );
}

function Call999Reminder({ emphatic }: { emphatic: boolean }) {
  return (
    <div style={{ ...reminderStyle, ...(emphatic ? reminderEmphaticStyle : null) }}>
      <div style={reminderLine1Style}>Call 999 first, then send.</div>
      <div style={reminderLine2Style}>First aiders are not a substitute for ambulance.</div>
    </div>
  );
}

function SendButton({
  canSend,
  sending,
  onClick,
}: {
  canSend: boolean;
  sending: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!canSend}
      style={{
        ...sendButtonStyle,
        cursor: canSend ? "pointer" : "not-allowed",
        opacity: canSend ? 1 : 0.55,
      }}
    >
      <SendGlyph size={20} color="#FFFFFF" />
      {sending ? "Sending…" : "Send alert to first aiders"}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const summaryStripStyle: CSSProperties = {
  background: "rgba(255,255,255,0.7)",
  border: `1px solid ${FA.redLt}`,
  borderRadius: 12,
  padding: "10px 14px",
  display: "flex",
  flexDirection: "column",
  gap: 8,
};

const summaryRowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
};

const summaryLabelStyle: CSSProperties = {
  fontFamily: F.body,
  fontSize: 9.5,
  fontWeight: 800,
  letterSpacing: 1,
  textTransform: "uppercase",
  color: FA.red,
};

const summaryValueStyle: CSSProperties = {
  fontFamily: F.mono,
  fontSize: 13,
  fontWeight: 700,
  color: C.ink,
  letterSpacing: 0.3,
};

const summaryNameStyle: CSSProperties = {
  fontFamily: F.body,
  fontSize: 13.5,
  fontWeight: 800,
  color: C.ink,
  letterSpacing: 0.1,
};

const summaryAddStyle: CSSProperties = {
  background: "none",
  border: "none",
  fontFamily: F.body,
  fontSize: 12.5,
  fontWeight: 800,
  color: FA.red,
  cursor: "pointer",
  padding: 0,
};

const fieldLabelStyle: CSSProperties = {
  fontFamily: F.body,
  fontSize: 10,
  fontWeight: 800,
  letterSpacing: 1,
  textTransform: "uppercase",
  color: FA.red,
  marginBottom: 7,
};

const inputStyle: CSSProperties = {
  width: "100%",
  padding: "12px 14px",
  background: "#FFFFFF",
  border: `1px solid ${FA.redLt}`,
  borderRadius: 11,
  fontFamily: F.body,
  fontSize: 15,
  fontWeight: 600,
  color: C.ink,
  outline: "none",
  boxSizing: "border-box",
};

const chipRowStyle: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: 7,
  marginTop: 8,
};

const chipStyle: CSSProperties = {
  padding: "7px 12px",
  background: "#FFFFFF",
  border: `1px solid ${FA.redLt}`,
  borderRadius: 999,
  fontFamily: F.body,
  fontSize: 12.5,
  fontWeight: 700,
  color: FA.redDk,
  cursor: "pointer",
};

// Selected chip — stays filled + highlighted so it's obvious which was tapped.
const chipSelectedStyle: CSSProperties = {
  ...chipStyle,
  background: FA.red,
  color: "#FFFFFF",
  border: `1px solid ${FA.red}`,
  boxShadow: "0 2px 8px rgba(184,54,42,0.25)",
};

const toggleBaseStyle: CSSProperties = {
  flex: 1,
  padding: 12,
  background: "#FFFFFF",
  border: `1px solid ${FA.redLt}`,
  borderRadius: 11,
  fontFamily: F.body,
  fontSize: 13,
  fontWeight: 800,
  letterSpacing: 0.6,
  color: C.inkSoft,
  cursor: "pointer",
  textTransform: "uppercase",
};

const toggleYesStyle: CSSProperties = {
  ...toggleBaseStyle,
  background: FA.red,
  color: "#FFFFFF",
  border: `1px solid ${FA.red}`,
  boxShadow: "0 2px 8px rgba(184,54,42,0.25)",
};

const toggleNoStyle: CSSProperties = {
  ...toggleBaseStyle,
  background: C.ink,
  color: "#FFFFFF",
  border: `1px solid ${C.ink}`,
};

const reminderStyle: CSSProperties = {
  background: FA.bg,
  border: `1px solid ${FA.redLt}`,
  borderRadius: 12,
  padding: "12px 14px",
};

const reminderEmphaticStyle: CSSProperties = {
  border: `1.5px solid ${FA.red}`,
  boxShadow: "0 2px 10px rgba(184,54,42,0.2)",
};

const reminderLine1Style: CSSProperties = {
  fontFamily: F.serif,
  fontSize: 15,
  fontWeight: 700,
  color: FA.redDeep,
  letterSpacing: -0.2,
};

const reminderLine2Style: CSSProperties = {
  fontFamily: F.body,
  fontSize: 12.5,
  fontWeight: 600,
  color: FA.redDk,
  marginTop: 2,
};

const sendButtonStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  background: `linear-gradient(180deg, ${FA.red} 0%, ${FA.redDk} 100%)`,
  color: "#FFFFFF",
  minHeight: 60,
  fontSize: 16,
  fontWeight: 900,
  letterSpacing: 0.6,
  textTransform: "uppercase",
  borderRadius: 12,
  border: "none",
  width: "100%",
  fontFamily: F.body,
  boxShadow: "0 4px 14px rgba(184,54,42,0.4), inset 0 1px 0 rgba(255,255,255,0.18)",
};

const captionButtonStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  width: "100%",
  background: "none",
  border: "none",
  fontFamily: F.body,
  fontSize: 12.5,
  fontWeight: 700,
  color: FA.redDk,
  cursor: "pointer",
  padding: "6px 0",
};

const tapHintStyle: CSSProperties = {
  fontWeight: 800,
  color: FA.red,
  textDecoration: "underline",
  textUnderlineOffset: 2,
};

const addPhoneStyle: CSSProperties = {
  width: "100%",
  background: "none",
  border: "none",
  fontFamily: F.body,
  fontSize: 13,
  fontWeight: 800,
  color: FA.red,
  cursor: "pointer",
  textAlign: "center",
};

const zeroBannerStyle: CSSProperties = {
  background: `linear-gradient(180deg, ${FA.red} 0%, ${FA.redDk} 100%)`,
  color: "#FFFFFF",
  borderRadius: 12,
  padding: "16px 18px",
  fontFamily: F.serif,
  fontSize: 17,
  fontWeight: 700,
  textAlign: "center",
  letterSpacing: -0.2,
  boxShadow: "0 4px 14px rgba(184,54,42,0.35)",
};

const errorStyle: CSSProperties = {
  background: C.signalBg,
  border: `1px solid ${FA.redLt}`,
  borderRadius: 10,
  padding: "10px 12px",
  fontFamily: F.body,
  fontSize: 13,
  fontWeight: 700,
  color: FA.redDk,
};
